import { formatINR, API, formatDate } from "../lib/api";
import { useState } from "react";
import { DriveDocuments } from "../components/DriveDocuments";
import { Landmark, AlertTriangle, Upload, Loader2, ShieldAlert, X, FileSpreadsheet, UploadCloud, Search, GitMerge, Zap, ArrowLeftRight } from "lucide-react";
import { Stat, FilterGroup } from "../components/bank/BankBits";
import { BankLedger } from "../components/bank/BankLedger";
import { TransferReviewModal } from "../components/bank/TransferReviewModal";
import { UploadedStatements } from "../components/bank/UploadedStatements";
import { BankInfoDialog } from "../components/bank/BankInfoDialog";
import { MergeAccountsDialog } from "../components/bank/MergeAccountsDialog";
import { AccountBalancesCards, MonthlySpendChart } from "../components/bank/BankInsights";
import { useBankTransactions } from "../hooks/useBankTransactions";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "untallied", label: "Untallied" },
  { key: "tallied", label: "Tallied" },
];
const TYPE_FILTERS = [
  { key: "", label: "All" },
  { key: "debit", label: "Debit" },
  { key: "credit", label: "Credit" },
];
const EXPENSE_CATEGORIES = ["Fuel", "Toll", "Maintenance", "Salary", "Office", "Misc"];

export default function BankStatements() {
  const {
    driveStatus, uploading, progress, phase, queue, statements, txns, summary, banks, accounts, loading, lrs,
    tamperNotice, setTamperNotice, openTally, setOpenTally, expenseForm, setExpenseForm,
    fBank, setFBank, fAccount, setFAccount, fStatus, setFStatus, fType, setFType,
    fStart, setFStart, fEnd, setFEnd, fMin, setFMin, fMax, setFMax, fQuery, setFQuery, resetFilters,
    bankPrompts, setBankPrompts, editStmt, setEditStmt, setStatementBank,
    bankAccounts, showMerge, setShowMerge, mergeAccounts,
    forceImport, setForceImport, flagStatement, reparseStatement,
    autoTally, autoTallying,
    autoTallyTransfers, autoTallyingTransfers, tallyPersonal, people,
    transferReview, setTransferReview, confirmTransfers,
    accountBalances, monthlySpend,
    onUpload, uploadStatements, tally, tallyRef, untally, createExpense, removeStatement, exportXlsx,
  } = useBankTransactions();
  const [dragOver, setDragOver] = useState(false);
  const btnLabel = uploading ? (phase === "parse" ? "Parsing…" : `Uploading ${progress}%`) : "Upload Statement";
  const overall = queue ? Math.round((((queue.index - 1) * 100) + (phase === "parse" ? 100 : progress)) / (queue.total * 100) * 100) : 0;
  const hasFilters = fBank || fAccount || fStatus || fType || fStart || fEnd || fMin !== "" || fMax !== "" || fQuery;

  return (
    <div data-testid="bank-statements-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Reconciliation</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Bank Statements</h1>
          <p className="text-sm text-[#68665E] mt-2">Upload statements (PDF / Excel / CSV) — all banks are combined into one date-wise ledger, checked for tampering, and tallied against your records.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="btn-primary cursor-pointer inline-flex items-center gap-2" data-testid="statement-upload-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {btnLabel}
            <input type="file" multiple accept=".pdf,.csv,.xlsx,.xls" className="hidden" onChange={onUpload} disabled={uploading} data-testid="statement-file-input" />
          </label>
          <button onClick={autoTally} disabled={autoTallying} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60" data-testid="auto-tally-btn" title="Auto-create expenses for untallied fuel & toll debits">
            {autoTallying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Auto-tally fuel &amp; toll
          </button>
          <button onClick={autoTallyTransfers} disabled={autoTallyingTransfers} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60" data-testid="auto-tally-transfers-btn" title="Auto-match internal transfers between your company accounts (same amount, same day)">
            {autoTallyingTransfers ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />} Auto-tally transfers
          </button>
          <button onClick={exportXlsx} className="btn-secondary inline-flex items-center gap-2" data-testid="export-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </button>
        </div>
      </div>

      {driveStatus && !driveStatus.connected && (
        <div className="mb-6 flex items-center gap-2 text-sm text-[#8A2526]" data-testid="drive-status-banner">
          <AlertTriangle className="w-4 h-4" /> Google Drive not connected — statement archiving is unavailable. {driveStatus.error || ""}
        </div>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!uploading) uploadStatements(e.dataTransfer.files); }}
        className={`mb-6 rounded border border-dashed p-4 transition-colors ${dragOver ? "border-[#C84B31] bg-[#FBEAE7]" : "border-[#D8D6CE]"} ${uploading ? "border-[#C84B31]" : ""}`}
        data-testid="statement-dropzone"
      >
        {uploading ? (
          <div className="w-full max-w-xl mx-auto" data-testid="upload-progress">
            <div className="flex items-center justify-between text-xs text-[#68665E] mb-1.5">
              <span className="inline-flex items-center gap-1.5 truncate">
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                <span className="truncate">
                  {queue && queue.total > 1 ? `File ${queue.index} of ${queue.total} · ${queue.name}` : (queue?.name || "Statement")}
                </span>
              </span>
              <span className="shrink-0 ml-3 font-medium" data-testid="upload-progress-label">
                {phase === "parse" ? "Parsing…" : `Uploading ${progress}%`}
              </span>
            </div>
            <div className="h-2 rounded-full bg-[#E5E3DB] overflow-hidden">
              <div
                className={`h-full bg-[#C84B31] transition-[width] duration-200 ${phase === "parse" ? "animate-pulse" : ""}`}
                style={{ width: `${phase === "parse" ? 100 : progress}%` }}
              />
            </div>
            {queue && queue.total > 1 && (
              <div className="mt-2.5" data-testid="upload-overall">
                <div className="flex items-center justify-between text-[11px] text-[#A09E96] mb-1">
                  <span>{queue.index} of {queue.total} files</span>
                  <span className="font-medium" data-testid="upload-overall-label">{overall}% overall</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#E5E3DB] overflow-hidden">
                  <div className="h-full bg-[#1A1A18] transition-[width] duration-200" style={{ width: `${overall}%` }} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-sm text-[#A09E96]">
            <div className="flex items-center gap-2">
              <UploadCloud className="w-4 h-4" />
              Drag & drop one or more statements here (PDF / Excel / CSV), or use the Upload button above
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-[#68665E] cursor-pointer mt-1" data-testid="force-import-toggle">
              <input type="checkbox" checked={forceImport} onChange={(e) => setForceImport(e.target.checked)} className="accent-[#C84B31]" />
              Force import — bring in every row even if it looks like a duplicate
            </label>
          </div>
        )}
      </div>

      {tamperNotice && (
        <div className="mb-6 border border-[#8A2526] bg-[#FBEDED] p-4" data-testid="tamper-notice">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-[#8A2526] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[#8A2526]">Possible tampering detected in {tamperNotice.bank_name}</div>
              <p className="text-sm text-[#8A2526] mt-1">The running balance does not add up on {tamperNotice.tamper_issues.length} row(s) — a transaction may have been altered or omitted. These rows are outlined red below.</p>
              <ul className="text-xs text-[#8A2526] mt-2 space-y-0.5">
                {tamperNotice.tamper_issues.slice(0, 5).map((t, i) => (
                  <li key={`${t.date}-${t.expected}-${t.actual}`}>{formatDate(t.date)} · {t.description} — expected balance {formatINR(t.expected)}, statement shows {formatINR(t.actual)}</li>
                ))}
              </ul>
            </div>
            <button onClick={() => setTamperNotice(null)}><X className="w-4 h-4 text-[#8A2526]" /></button>
          </div>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="reconcile-summary">
          <Stat label="Transactions" value={summary.total} />
          <Stat label="Tallied" value={summary.tallied} tone="green" />
          <Stat label="Untallied" value={summary.untallied} tone="red" />
          <Stat label="Total Debit" value={formatINR(summary.total_debit)} />
          <Stat label="Total Credit" value={formatINR(summary.total_credit)} tone="green" />
        </div>
      )}

      {summary && summary.total > 0 && (() => {
        const pct = Math.round((summary.tallied / summary.total) * 100);
        const tone = pct >= 90 ? "#1f8a4c" : pct >= 60 ? "#C08A2B" : "#8A2526";
        return (
          <div className="border border-[#E5E3DB] bg-white rounded p-4 mb-6" data-testid="tally-health-widget">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Tally Health</div>
              <div className="text-sm font-bold" style={{ color: tone }} data-testid="tally-health-pct">{pct}% reconciled</div>
            </div>
            <div className="h-2.5 w-full rounded-full bg-[#EFEDE6] overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: tone }} data-testid="tally-health-bar" />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs">
              <span className="text-[#1f8a4c] font-medium">{summary.tallied} matched</span>
              <button onClick={() => setFStatus("untallied")} className="text-[#8A2526] font-medium hover:underline" data-testid="tally-health-untallied">
                {summary.untallied} still untallied →
              </button>
            </div>
          </div>
        );
      })()}

      <AccountBalancesCards balances={accountBalances} />

      {/* Filters */}
      <div className="border border-[#E5E3DB] bg-white p-3 mb-4 rounded" data-testid="bank-filters">
        <div className="flex flex-wrap items-center gap-3">
          <select value={fAccount} onChange={(e) => setFAccount(e.target.value)} className="input-field w-auto" data-testid="filter-account">
            <option value="">All Accounts</option>
            {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={() => setShowMerge(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#68665E] border border-[#E5E3DB] px-2.5 py-2 rounded hover:border-[#C84B31] hover:text-[#C84B31] transition-colors" data-testid="open-merge-accounts" title="Merge two accounts">
            <GitMerge className="w-3.5 h-3.5" /> Merge accounts
          </button>
          <div className="relative">
            <Search className="w-4 h-4 text-[#A09E96] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={fQuery} onChange={(e) => setFQuery(e.target.value)} placeholder="Search description…"
              className="input-field w-auto pl-8" data-testid="filter-query" />
          </div>
          <FilterGroup options={STATUS_FILTERS} value={fStatus} onChange={setFStatus} testid="filter-status" />
          <FilterGroup options={TYPE_FILTERS} value={fType} onChange={setFType} testid="filter-type" />
          {summary && summary.tampered > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-[#8A2526] font-medium ml-auto">
              <ShieldAlert className="w-4 h-4" /> {summary.tampered} tampered row(s)
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <label className="text-xs text-[#68665E] font-medium">From
            <input type="date" value={fStart} onChange={(e) => setFStart(e.target.value)} className="input-field w-auto ml-1.5" data-testid="filter-start" />
          </label>
          <label className="text-xs text-[#68665E] font-medium">To
            <input type="date" value={fEnd} onChange={(e) => setFEnd(e.target.value)} className="input-field w-auto ml-1.5" data-testid="filter-end" />
          </label>
          <label className="text-xs text-[#68665E] font-medium">Min ₹
            <input type="number" value={fMin} onChange={(e) => setFMin(e.target.value)} placeholder="0" className="input-field w-24 ml-1.5" data-testid="filter-min" />
          </label>
          <label className="text-xs text-[#68665E] font-medium">Max ₹
            <input type="number" value={fMax} onChange={(e) => setFMax(e.target.value)} placeholder="Any" className="input-field w-24 ml-1.5" data-testid="filter-max" />
          </label>
          {hasFilters && (
            <button onClick={resetFilters} className="text-xs text-[#C84B31] hover:opacity-70 font-medium ml-auto" data-testid="filter-clear">
              Clear all filters
            </button>
          )}
        </div>
      </div>

      <MonthlySpendChart data={monthlySpend} />

      <BankLedger loading={loading} txns={txns} openTally={openTally} setOpenTally={setOpenTally}
        expenseForm={expenseForm} setExpenseForm={setExpenseForm}
        tally={tally} tallyRef={tallyRef} untally={untally} createExpense={createExpense} categories={EXPENSE_CATEGORIES} lrs={lrs}
        tallyPersonal={tallyPersonal} people={people} />

      {transferReview && (
        <TransferReviewModal review={transferReview} onClose={() => setTransferReview(null)} confirmTransfers={confirmTransfers} />
      )}

      <UploadedStatements statements={statements} removeStatement={removeStatement} onEdit={setEditStmt} onFlag={flagStatement} onReparse={reparseStatement} />

      <div className="bg-white border border-[#E5E3DB] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="w-5 h-5 text-[#C84B31]" />
          <div className="section-title mb-0">Statement Archive (Google Drive)</div>
        </div>
        <DriveDocuments category="bank" refId="" refLabel="Bank Statements" docTypes={["Statement", "Other"]} />
      </div>

      {bankPrompts.length > 0 && (
        <BankInfoDialog
          statement={bankPrompts[0]}
          mode="prompt"
          onSave={(vals) => setStatementBank(bankPrompts[0], vals)}
          onClose={() => setBankPrompts(bankPrompts.slice(1))}
        />
      )}
      {editStmt && (
        <BankInfoDialog
          statement={editStmt}
          mode="edit"
          onSave={(vals) => setStatementBank(editStmt, vals)}
          onClose={() => setEditStmt(null)}
        />
      )}
      {showMerge && (
        <MergeAccountsDialog
          accounts={bankAccounts}
          onMerge={mergeAccounts}
          onClose={() => setShowMerge(false)}
        />
      )}
    </div>
  );
}
